import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Input, Spinner, Label, Select } from '@iq/react-components';

import { getActiveSite } from '../../../bundles/sites';
import { getHasPermission } from '../../../bundles/auth';
import { getTags, updateTag, deleteTags, requestTags } from '../../../bundles/tags';
import { displayNotification } from '../../../bundles/notifications';
import { getFiles, getUserEvents, getVisualizations } from '../../../services';
import { TAG_CATEGORIES } from '../../../constants';
import config from '../../../config';

import ConfirmationDialog from '../../ConfirmationDialog';
import TagSelect from '../../TagSelect';
import Heading from '../../Heading';

const getAssociationStatusText = (connectedEntities, selectedCategory) => {
  if (connectedEntities > 0) {
    const categoryLabel =
      connectedEntities === 1 ? selectedCategory.slice(0, -1) : selectedCategory;
    return `Used in ${connectedEntities} ${categoryLabel}`;
  }

  return `Not used in ${selectedCategory}`;
};

const TagsView = () => {
  const dispatch = useDispatch();

  const site = useSelector(getActiveSite);
  const canDeleteTags = useSelector((state) =>
    getHasPermission(state, 'tags/Delete', { org: site.org, site: site.id })
  );
  const tags = useSelector(getTags);
  const categoryList = config.onPremise
    ? TAG_CATEGORIES.filter((cat) => cat !== 'files')
    : TAG_CATEGORIES;
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(categoryList[0]);
  const [tagsToDelete, setTagsToDelete] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [tagName, setTagName] = useState('');
  const [checkingTags, setCheckingTags] = useState(false);
  const selectedTagId = selectedTagIds[0];
  const selectedTag = tags.find((tag) => tag.id === selectedTagId);
  const isSingleTagSelected = selectedTagIds.length === 1;
  const trimmedTagName = tagName.trim();

  useEffect(() => {
    if (site.id) {
      dispatch(requestTags(site.id));
    }
  }, [dispatch, site.id]);

  useEffect(() => setIsEditing(false), [tags]);

  const onCategorySelect = useCallback(
    (option) => {
      if (!option?.value) {
        return;
      }

      setSelectedCategory(option.value);
    },
    [setSelectedCategory]
  );

  useEffect(() => {
    setSelectedTagIds([]);
  }, [selectedCategory]);

  const getConnectedEntityCountForCategory = useCallback(
    async (tagId) => {
      const siteScope = {
        site: site.id,
        org: site.org,
      };

      switch (selectedCategory) {
        case 'events': {
          const { total } = await getUserEvents({
            ...siteScope,
            tags: [tagId],
          });

          return Number(total) || 0;
        }
        case 'files': {
          const { total } = await getFiles({
            ...siteScope,
            tags: [
              {
                filterType: 'include',
                operator: 'or',
                tags: [tagId],
              },
            ],
          });

          return Number(total) || 0;
        }
        case 'visualizations': {
          const { total } = await getVisualizations({
            site: site.id,
            tags: [tagId],
          });

          return Number(total) || 0;
        }
        default: {
          return 0;
        }
      }
    },
    [selectedCategory, site.id, site.org]
  );

  const onStartEditing = useCallback(() => {
    if (!selectedTag) {
      return;
    }

    setTagName(selectedTag.name || '');
    setIsEditing(true);
  }, [selectedTag]);

  const onDeleteTags = useCallback(async () => {
    setCheckingTags(true);
    try {
      const checkedTags = await Promise.all(
        selectedTagIds.map(async (tagId) => {
          try {
            const connectedEntities = await getConnectedEntityCountForCategory(tagId);

            return {
              ...tags.find((tag) => tag.id === tagId),
              connectedEntities,
            };
          } catch (error) {
            return { error };
          }
        })
      );

      if (checkedTags.some((tag) => tag.error)) {
        dispatch(
          displayNotification({
            title: 'Unable to delete tags',
            message: 'Encountered error when checking for entities connected to tags.',
            type: 'persistent',
            status: 'warning',
            id: 'delete-tag',
          })
        );
      } else {
        setTagsToDelete(() => checkedTags.filter((tag) => tag.id));
      }
    } finally {
      setCheckingTags(false);
    }
  }, [dispatch, getConnectedEntityCountForCategory, selectedTagIds, tags]);

  const confirmDeleteBody = checkingTags ? (
    <div className="checking-tags">
      <Spinner size="s" />
      <span>Finding tag associations...</span>
    </div>
  ) : (
    <>
      <p>{`Are you sure you want to delete the following tag${tagsToDelete.length > 1 ? 's' : ''}?`}</p>
      <div className="tag-container">
        {tagsToDelete.map((tag) => (
          <div
            key={tag.id}
            className="tag-connections"
          >
            <span>{tag.name}</span>
            <span>{getAssociationStatusText(tag.connectedEntities, selectedCategory)}</span>
          </div>
        ))}
      </div>
    </>
  );

  const onConfirmDeleteTags = useCallback(() => {
    dispatch(
      deleteTags(
        site.id,
        tagsToDelete.map((tag) => tag.id),
        selectedCategory
      )
    );
    setTagsToDelete([]);
    setSelectedTagIds([]);
  }, [dispatch, tagsToDelete, site.id, selectedCategory]);

  const confirmUpdateBody = selectedTag ? (
    <div className="tag-edit-modal">
      {selectedTag.categories?.length > 1 && (
        <>
          <p>{`This tag belongs to the following categories: ${selectedTag.categories.join(', ')}.`}</p>
          <p>{`Changes will be reflected in this tag across all categories.`}</p>
        </>
      )}
      <div className="tag-edit-modal__input">
        <Label htmlFor="tag-name">Tag name</Label>
        <Input
          id="tag-name"
          value={tagName}
          onChange={(event) => setTagName(event.target.value)}
          required
        />
      </div>
    </div>
  ) : null;

  const onUpdateTag = useCallback(() => {
    if (!selectedTag || trimmedTagName.length === 0) {
      return;
    }

    const tagUpdate = { ...selectedTag, name: trimmedTagName };
    // This screen only edits the tag name, so do not send optional presentation fields.
    delete tagUpdate.color;
    delete tagUpdate.icon;

    dispatch(updateTag(tagUpdate));
  }, [dispatch, selectedTag, trimmedTagName]);

  const categoryOptions = categoryList.map((cat) => ({
    value: cat,
    label: cat.charAt(0).toUpperCase() + cat.slice(1),
  }));
  const categoryValue = categoryOptions.find((opt) => opt.value === selectedCategory);

  return (
    <div className="tags-view">
      <Heading contentLeft={<div className="title">Create, edit or delete tags</div>} />
      <div className="tags-view__row">
        <div className="tags-view__select-container">
          <Label htmlFor="category-select">Category</Label>
          <Select
            id="category-select"
            label="Category"
            isMulti={false}
            isClearable={false}
            onChange={onCategorySelect}
            creatable={false}
            value={categoryValue}
            options={categoryOptions}
            closeMenuOnSelect
            placeholder="Select category"
          />
        </div>
      </div>
      <div className="tags-view__row">
        <div className="tags-view__select-container">
          <Label htmlFor="tag-select">{'Tag(s)'}</Label>
          <TagSelect
            id="tag-select"
            category={selectedCategory}
            value={selectedTagIds}
            onChange={setSelectedTagIds}
            placeholder="Create or select tags"
            isDisabled={isEditing}
          />
        </div>
      </div>
      <div className="tags-view__tag-actions">
        <Button
          activity="danger"
          type="button"
          onClick={onDeleteTags}
          disabled={selectedTagIds.length === 0 || !canDeleteTags}
        >
          Delete
        </Button>
        <Button
          activity="primary"
          type="button"
          onClick={onStartEditing}
          disabled={!isSingleTagSelected}
        >
          Edit
        </Button>
      </div>

      {checkingTags || tagsToDelete.length > 0 ? (
        <ConfirmationDialog
          onCancel={() => setTagsToDelete([])}
          onConfirm={onConfirmDeleteTags}
          confirmType="danger"
          title="Delete Tags"
          body={confirmDeleteBody}
          confirmText="Delete"
          disabled={checkingTags}
          showCancel
        />
      ) : null}

      {isEditing ? (
        <ConfirmationDialog
          onCancel={() => setIsEditing(false)}
          onConfirm={onUpdateTag}
          confirmType="primary"
          title="Edit Tag"
          body={confirmUpdateBody}
          confirmText="Update"
          disabled={checkingTags || trimmedTagName.length === 0}
          showCancel
        />
      ) : null}
    </div>
  );
};

export default TagsView;
